import { Dialog, Portal } from "@ark-ui/react";
import classnames from "classnames";
import type { ReactNode } from "react";

import { useTranslation } from "@/lib/i18n";
import Button from "@/components/button/button";
import { Icon } from "@/components/icon/icon";
import ModalMapPreview from "@/features/widget-forms/shared/modal-map-preview/modal-map-preview";
import type { Configuration } from "@/features/widget-forms/forms/weather-model-map/types";
import dialogVariants from "@/components/dialog/dialog-variants.module.css";
import { Badge } from "@/components/label/label";

import modalStyles from "./modal-styles.module.css";
import styles from "./preview-modal.module.css";

interface PreviewModalProps {
  /** Only used for the placeholder fallback (model-map). */
  configuration?: Configuration;
  /** Full preview content (e.g. the live obs-map widget). Falls back to the
   *  static placeholder when omitted. */
  previewNode?: ReactNode;
  /**
   * Optional controls rendered in the dialog header, next to the close button.
   * Each caller composes the controls it needs (e.g. a
   * {@link WidgetContextToolbar} with time-range / refresh / station pickers).
   * The toolbar and {@link previewNode} must be mounted within the same widget
   * runtime (wrap the dialog in a `WidgetContextHost`) so the controls drive the
   * previewed widget — context crosses the portal.
   */
  toolbar?: ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
}

const PreviewModal = ({
  configuration,
  previewNode,
  toolbar,
  open,
  onOpenChange,
  title,
}: PreviewModalProps) => {
  const { t } = useTranslation();
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(details) => onOpenChange(details.open)}
      lazyMount
      unmountOnExit
    >
      <Portal>
        <Dialog.Backdrop className={modalStyles.nestedModalBackdrop} />
        <Dialog.Positioner className={modalStyles.nestedModalPositioner}>
          <Dialog.Content
            className={classnames(dialogVariants.fullscreen, styles.previewModalContent)}
          >
            <div
              data-scope="dialog"
              data-part="header"
              className={styles.previewModalHeader}
            >
              <Dialog.Title asChild>
                <span className={styles.previewModalTitle}>
                  <Badge>{t("admin.widgets.fields.map_editor.preview.title")}</Badge>
                  {title}
                </span>
              </Dialog.Title>
              <div className={styles.previewModalHeaderActions}>
                {toolbar && (
                  <div className={styles.previewModalHeaderToolbar}>{toolbar}</div>
                )}
                <Dialog.CloseTrigger asChild>
                  <Button variant="ghost">
                    <Icon name="x" />
                  </Button>
                </Dialog.CloseTrigger>
              </div>
            </div>
            <div className={styles.previewModalBody}>
              {previewNode ? (
                <div className={styles.previewModalLiveHost}>{previewNode}</div>
              ) : (
                <ModalMapPreview configuration={configuration} />
              )}
            </div>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default PreviewModal;
